import dynamic from 'next/dynamic';
import { useContext } from 'react';
import { MessageDescriptor } from 'react-intl';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import { OfferBannerContext } from '@/components/app/layout/OfferBannerProvider';
import BlogFeed from '@/components/homepageRefresh/homepage/BlogFeed';
import FAQ from '@/components/homepageRefresh/homepage/FAQ';
import MentalHealthTools, {
	MentalHealthToolsProps,
} from '@/components/homepageRefresh/homepage/MentalHealthTools';
import PlanToggle from '@/components/homepageRefresh/homepage/PlanToggle';
import Testimonials, { TestimonialsProps } from '@/components/homepageRefresh/homepage/Testimonials';
import { BlogCategory } from '@/utils/blogPosts';

import type { BreatheBubbleProps } from '../BreatheBubble';
import Hero, { SubpageHeroProps } from '../Hero';
import InstructorVideo, { InstructorVideoProps } from '../InstructorVideo';
import Unlock, { SubpageUnlockProps } from '../Unlock';
import { Wrapper } from './styles';

// This avoids the `document is not defined` error during SSR
// See: https://github.com/airbnb/lottie-web/issues/3047
const BreatheBubble = dynamic(() => import('../BreatheBubble'), { ssr: false });

const SubpageLayout = ({
	heroHeading,
	heroBody,
	heroCta,
	heroBgImage,
	mediaSectionType,
	mediaSectionHeading,
	instructorVideoSrc,
	instructorVideoCaptionsSrc,
	instructorVideoThumbnail,
	unlockHeading,
	unlockBody,
	unlockBgImage,
	plansBgImage,
	reviewsHeading,
	reviews,
	blogHeading,
	blogCategory,
	toolsSections,
	toolsHeading,
	FaqSections,
}: {
	blogCategory: BlogCategory;
	mediaSectionType: 'instructor' | 'bubble';
	blogHeading: MessageDescriptor;
	plansBgImage: string;
	mediaSectionHeading: MessageDescriptor;
	FaqSections: React.FC;
} & SubpageHeroProps &
	SubpageUnlockProps &
	TestimonialsProps &
	MentalHealthToolsProps &
	Partial<InstructorVideoProps> &
	Partial<BreatheBubbleProps>) => {
	const { isBannerVisible } = useContext(OfferBannerContext);

	return (
		<Wrapper id={MAIN_CONTENT_ID} isBannerVisible={isBannerVisible}>
			<Hero heroHeading={heroHeading} heroBody={heroBody} heroCta={heroCta} heroBgImage={heroBgImage} />
			{mediaSectionType === 'bubble' && <BreatheBubble breatheBubbleHeading={mediaSectionHeading} />}
			{mediaSectionType === 'instructor' &&
				instructorVideoThumbnail &&
				instructorVideoSrc &&
				instructorVideoCaptionsSrc && (
					<InstructorVideo
						instructorVideoCaptionsSrc={instructorVideoCaptionsSrc}
						instructorVideoHeading={mediaSectionHeading}
						instructorVideoThumbnail={instructorVideoThumbnail}
						instructorVideoSrc={instructorVideoSrc}
					/>
				)}
			<MentalHealthTools
				toolsSections={toolsSections}
				toolsHeading={toolsHeading}
				analyticsPrefix="Subpage"
			/>
			<Unlock unlockHeading={unlockHeading} unlockBody={unlockBody} unlockBgImage={unlockBgImage} />
			<BlogFeed heading={blogHeading} category={blogCategory} analyticsPrefix="Subpage" />
			<Testimonials reviewsHeading={reviewsHeading} reviews={reviews} />
			<PlanToggle backgroundUrl={plansBgImage} analyticsPrefix="Subpage" />
			<FAQ FaqSections={FaqSections} />
		</Wrapper>
	);
};

export default SubpageLayout;
